// src/features/locations/SpaceFitOverviewPane.jsx
//
// OFFICE-OVERVIEW-REMOVE-SCORE: Standalone Space Fit pane for the 2×2 Overview
// grid (bottom-right). Replaces the removed Location Score card.
//
// OFFICE-OVERVIEW-SPACEFIT-PANE-ENRICH: Added two-column layout, mini scatter,
// Recommended line, and As-of date in the panel header.
//
// Left column:
//   - RAG square + verdict word + band descriptor
//   - 3 driver tiles: Assignment % · Peak day % · Headroom count
//   - Rule-derived "Recommended" one-liner
//
// Right column:
//   - SpaceFitMiniScatter — compact SVG plotting all portfolio offices as
//     verdict-colored dots; current office highlighted with a ring + "SF" label
//
// All tokens are sourced from spaceFitVerdict.js — identical to the list pill,
// drawer, and map marker so the verdict is visually consistent everywhere.
// No ring, no 0-100 score.
//
// Props:
//   location        — full location object (installedCapacity, utilization[], etc.)
//   latestHeadcount — { total: number } | null

import React, { useEffect, useState } from "react";
import { T } from "../../app/tokens.js";
import {
  computeSpaceFit,
  SPACE_FIT_BG,
  SPACE_FIT_HEX,
  SPACE_FIT_LABEL,
  SPACE_FIT_VERDICT_OVER,
  SPACE_FIT_VERDICT_DENSE,
  SPACE_FIT_VERDICT_UNDER,
  SPACE_FIT_VERDICT_RIGHT,
  DESK_BALANCED_MIN,
  DESK_OVER_THRESHOLD,
} from "../../utils/spaceFitVerdict.js";
import {
  COMFORT_TIGHT_MIN_SF_PER_SEAT,
  COMFORT_ROOMY_MIN_SF_PER_SEAT,
} from "../../utils/comfortDensity.js";
import { getAnalyticalSqft as getDisplayedSqft } from "../../utils/locationSqftDerived.js";
import { getSpaceFitReport } from "../../data/reportsClient.js";
import { ReportingPeriodLabel } from "../../components/ReportingPeriodLabel.jsx";
import { formatReportingMonth } from "../../services/reportingPeriod.js";
import { officeAttendanceCaption } from "./officeMetricVintages.js";

// ── Verdict reasons ───────────────────────────────────────────────────────────

const VERDICT_REASON = {
  [SPACE_FIT_VERDICT_OVER]:  "More people assigned than installed seats",
  [SPACE_FIT_VERDICT_DENSE]: "Below 150 sf per seat – physically cramped",
  [SPACE_FIT_VERDICT_UNDER]: `Desk demand below ${DESK_BALANCED_MIN * 100}% of installed seats`,
  [SPACE_FIT_VERDICT_RIGHT]: `Balanced desk fill between ${DESK_BALANCED_MIN * 100}% and 100%`,
};

// ── Recommended rule map ──────────────────────────────────────────────────────

export const VERDICT_RECOMMENDED = {
  [SPACE_FIT_VERDICT_RIGHT]: "Monitor - no action needed. Recheck when headcount or capacity changes.",
  [SPACE_FIT_VERDICT_UNDER]: "Consider right-sizing or subletting to reduce underused capacity.",
  [SPACE_FIT_VERDICT_DENSE]: "Flag for remediation - build density is physically cramped.",
  [SPACE_FIT_VERDICT_OVER]:  "Prioritise expansion options or reduce headcount assignment.",
};

// ── Mini scatter chart constants ──────────────────────────────────────────────

const MINI_W   = 280;
const MINI_H   = 200;
const MINI_L   = 32;
const MINI_R   = 270;
const MINI_T   = 12;
const MINI_B   = 165;
const MINI_PW  = MINI_R - MINI_L;  // 238
const MINI_PH  = MINI_B - MINI_T;  // 153
const MINI_X_MAX = 160; // % desk demand
const MINI_Y_MAX = 450; // SF/seat

function mxScale(pct)  { return MINI_L + (Math.min(Math.max(pct, 0), MINI_X_MAX) / MINI_X_MAX) * MINI_PW; }
function myScale(sfps) { return MINI_B - (Math.min(Math.max(sfps, 0), MINI_Y_MAX) / MINI_Y_MAX) * MINI_PH; }

// ── SpaceFitMiniScatter ───────────────────────────────────────────────────────

function SpaceFitMiniScatter({ offices, currentLocationId }) {
  const plotOffices = offices.filter(
    (o) => o.sf != null && o.seats != null && o.people != null && o.seats > 0
  );

  // Separate current from others; render current last so it sits on top
  const otherOffices  = plotOffices.filter((o) => o.id !== currentLocationId);
  const currentOffice = plotOffices.find((o) => o.id === currentLocationId) ?? null;

  const xBalanced = mxScale(DESK_BALANCED_MIN * 100);
  const x100 = mxScale(DESK_OVER_THRESHOLD * 100);
  const y150 = myScale(COMFORT_TIGHT_MIN_SF_PER_SEAT);
  const y250 = myScale(COMFORT_ROOMY_MIN_SF_PER_SEAT);

  return (
    <div data-testid="sf-mini-scatter-wrap">
      <svg
        data-testid="sf-mini-scatter"
        viewBox={`0 0 ${MINI_W} ${MINI_H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        aria-label="Space Fit mini scatter chart"
      >
        {/* Axes */}
        <line x1={MINI_L} y1={MINI_T} x2={MINI_L} y2={MINI_B} stroke={T.border} strokeWidth={1} />
        <line x1={MINI_L} y1={MINI_B} x2={MINI_R} y2={MINI_B} stroke={T.border} strokeWidth={1} />

        {/* Y-axis tick labels */}
        {[0, 150, 250].map((v) => (
          <g key={v}>
            <line x1={MINI_L - 3} y1={myScale(v)} x2={MINI_L} y2={myScale(v)} stroke={T.border} strokeWidth={0.8} />
            <text x={MINI_L - 5} y={myScale(v) + 3} textAnchor="end" fontSize={7} fill={T.mid}>{v}</text>
          </g>
        ))}

        {/* X-axis tick labels */}
        {[0, 50, 100, 150].map((v) => (
          <g key={v}>
            <line x1={mxScale(v)} y1={MINI_B} x2={mxScale(v)} y2={MINI_B + 3} stroke={T.border} strokeWidth={0.8} />
            <text x={mxScale(v)} y={MINI_B + 10} textAnchor="middle" fontSize={7} fill={T.mid}>{v}%</text>
          </g>
        ))}

        {/* Dashed reference: balanced desk demand minimum (gray) */}
        <line
          data-testid="sf-mini-balanced-line"
          x1={xBalanced} y1={MINI_T} x2={xBalanced} y2={MINI_B}
          stroke={T.light} strokeWidth={0.8} strokeDasharray="3 2"
        />
        <text x={xBalanced + 2} y={MINI_T + 7} fontSize={6} fill={T.mid}>
          {DESK_BALANCED_MIN * 100}%
        </text>

        {/* Dashed reference: 100% desk demand (red) */}
        <line
          x1={x100} y1={MINI_T} x2={x100} y2={MINI_B}
          stroke="rgba(194,16,46,0.5)" strokeWidth={1} strokeDasharray="3 2"
        />

        {/* Dashed reference: y=250 SF/seat (gray) */}
        <line
          x1={MINI_L} y1={y250} x2={MINI_R} y2={y250}
          stroke={T.light} strokeWidth={0.8} strokeDasharray="3 2"
        />

        {/* Dashed reference: y=150 SF/seat (gray) */}
        <line
          x1={MINI_L} y1={y150} x2={MINI_R} y2={y150}
          stroke={T.light} strokeWidth={0.8} strokeDasharray="3 2"
        />

        {/* Target zone: balanced desk demand × 150–250 SF/seat */}
        <rect
          data-testid="sf-mini-target-zone"
          x={xBalanced}
          y={y250}
          width={x100 - xBalanced}
          height={y150 - y250}
          fill="rgba(31,157,107,0.10)"
          stroke="rgba(31,157,107,0.55)"
          strokeWidth={1.3}
          rx={3}
        />
        <text
          x={(xBalanced + x100) / 2}
          y={y250 - 2}
          textAnchor="middle"
          fontSize={6}
          fontWeight="bold"
          fill="rgba(31,157,107,0.75)"
        >
          Target {DESK_BALANCED_MIN * 100}–100%
        </text>

        {/* Regular office dots — muted to ~27% opacity so the current-office dot stands out */}
        {otherOffices.map((o) => {
          const xPct = (o.people / o.seats) * 100;
          const yVal = o.sf / o.seats;
          const cx = mxScale(xPct);
          const cy = myScale(yVal);
          const fill = o.verdict ? SPACE_FIT_HEX[o.verdict] : T.light;
          return (
            <circle
              key={o.id}
              data-testid={`sf-mini-dot-${o.id}`}
              cx={cx}
              cy={cy}
              r={4}
              fill={fill}
              stroke="white"
              strokeWidth={1}
              opacity={0.27}
            />
          );
        })}

        {/* Current office dot — rendered last (on top), larger with ring */}
        {currentOffice && (() => {
          const xPct = (currentOffice.people / currentOffice.seats) * 100;
          const yVal = currentOffice.sf / currentOffice.seats;
          const cx = mxScale(xPct);
          const cy = myScale(yVal);
          const ringColor = currentOffice.verdict ? SPACE_FIT_HEX[currentOffice.verdict] : T.light;
          return (
            <g key={currentOffice.id} data-testid="sf-mini-dot-current">
              <circle cx={cx} cy={cy} r={7.5} fill="none" stroke={ringColor} strokeWidth={2} />
              <circle cx={cx} cy={cy} r={5.5} fill="white" stroke={ringColor} strokeWidth={1.5} />
              <text
                x={cx}
                y={cy - 9}
                textAnchor="middle"
                fontSize={7}
                fontWeight="bold"
                fill={ringColor}
              >
                SF
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PanelHeading({ text, asOf, reportingPeriod }) {
  return (
    <div
      data-testid="sf-pane-heading"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "4px 12px",
        margin: "0 -20px",
        padding: "8px 20px",
        minHeight: 40,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <div
          style={{
            width: 3,
            height: 16,
            borderRadius: 2,
            background: T.blue,
            flexShrink: 0,
          }}
        />
        <h3 style={{ margin: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
          {text}
        </h3>
      </div>
      <ReportingPeriodLabel
        reportingPeriod={reportingPeriod}
        testId="sf-pane-as-of"
        infoTestId="sf-pane-coverage-info"
        copyVariant="overview"
        style={{ fontSize: T.fsXs, color: T.light }}
      >
        {asOf ? `As of ${asOf}` : "As of unknown"}
      </ReportingPeriodLabel>
    </div>
  );
}

function DriverTile({ label, value, testId, valueColor, attendanceCaption }) {
  return (
    <div
      data-testid={testId}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        padding: "10px 0",
      }}
    >
      <span
        style={{
          fontSize: T.fsXxxs5,
          fontWeight: T.fwBold,
          color: T.mid,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: T.fsLg5,
          fontWeight: T.fwDisplay,
          color: valueColor ?? T.dark,
          lineHeight: 1.1,
        }}
      >
        {value}
      </span>
      {attendanceCaption && (
        <span data-testid="sf-pane-attendance-caption" style={{ fontSize: T.fsXxxs, color: T.light, lineHeight: 1.3 }}>
          {`Monthly avg · ${attendanceCaption}`}
        </span>
      )}
    </div>
  );
}

// ── SpaceFitOverviewPane ──────────────────────────────────────────────────────

export function SpaceFitOverviewPane({ location, latestHeadcount, badgeIntegrationPending = false, showScatter = true }) {
  const sqft   = getDisplayedSqft(location, null);
  const seats  = location?.installedCapacity != null ? Number(location.installedCapacity) : null;
  const people = latestHeadcount?.total != null ? Number(latestHeadcount.total) : null;

  // Peak badges: scan all utilisation rows (ASC order) and pick the most-recent
  // month that carries a positive peakDailyUniqueBadges value.  The last array
  // element may have a null peak (badge feed paused), so we cannot rely on it
  // exclusively. Keep this numeric selection independent of caption metadata.
  const utilRows = Array.isArray(location?.utilization) ? location.utilization : [];
  const peakUtil = (() => {
    let best = null;
    for (const row of utilRows) {
      const p = row.peakDailyUniqueBadges;
      if (p == null || p <= 0) continue;
      if (best === null) { best = row; continue; }
      const rawBest = String(best.month ?? "").split("T")[0];
      const rawRow  = String(row.month  ?? "").split("T")[0];
      if (rawRow > rawBest) best = row;
    }
    return best;
  })();
  const hasPeakData = peakUtil !== null;
  const peakBadges  = hasPeakData ? peakUtil.peakDailyUniqueBadges : null;

  // Structural context shares Capacity & occupancy's snapshot, not attendance.
  // Keep the attendance caption and numeric selection independent.
  const asOf = formatReportingMonth(latestHeadcount?.snapshotDate);
  const attendanceCaption = officeAttendanceCaption(location);

  const { verdict } = computeSpaceFit(sqft, seats, people);

  const bgColor  = SPACE_FIT_BG[verdict]  ?? SPACE_FIT_BG[null];
  const hexColor = SPACE_FIT_HEX[verdict] ?? SPACE_FIT_HEX[null];
  const reason   = verdict ? VERDICT_REASON[verdict] : null;

  // Driver tile values
  const assignmentPct = (seats && seats > 0 && people != null)
    ? Math.round((people / seats) * 100)
    : null;
  const peakDayPct = (seats && seats > 0 && peakBadges != null)
    ? Math.round((peakBadges / seats) * 100)
    : null;
  const headroom = (seats != null && people != null) ? seats - people : null;

  // Scatter data from portfolio report (skipped when seats are absent — empty state)
  const [scatterOffices, setScatterOffices] = useState(null);
  useEffect(() => {
    if (!seats || !showScatter) return; // Drawer keeps the portfolio chart on the secondary reader.
    let cancelled = false;
    getSpaceFitReport()
      .then(({ offices }) => {
        if (!cancelled) setScatterOffices(offices);
      })
      .catch(() => {
        // Soft failure — scatter simply doesn't render; left column stays intact
      });
    return () => { cancelled = true; };
  }, [seats, showScatter]);

  const currentLocationId = location?.id ?? null;

  // Empty state: no seat data to derive a verdict
  if (!seats) {
    return (
      <div
        data-testid="space-fit-overview-pane"
        style={{
          borderRadius: 10,
          padding: "0 20px 16px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <PanelHeading
          text="Space fit"
          asOf={asOf}
          reportingPeriod={location?.reportingPeriod}
        />
        <div
          data-testid="space-fit-empty-state"
          style={{
            marginTop: 20,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 8,
              background: SPACE_FIT_BG[null],
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: T.fsXs, color: T.light, fontStyle: "italic" }}>
            No seat data - Space Fit unavailable
          </span>
        </div>
        {attendanceCaption && (
          <div data-testid="sf-pane-attendance-caption" style={{ fontSize: T.fsXxxs, color: T.light, lineHeight: 1.3, marginTop: 3 }}>
            {`Attendance · Monthly avg · ${attendanceCaption}`}
          </div>
        )}
      </div>
    );
  }

  const recommended = verdict ? VERDICT_RECOMMENDED[verdict] : null;
  const hasScatter  = scatterOffices != null && scatterOffices.filter(
    (o) => o.sf != null && o.seats != null && o.people != null
  ).length > 0;

  return (
    <div
      data-testid="space-fit-overview-pane"
      style={{
        borderRadius: 10,
        padding: "0 20px 16px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <PanelHeading
        text="Space fit"
        asOf={asOf}
        reportingPeriod={location?.reportingPeriod}
      />

      {/* Two-column body */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: showScatter ? "1fr 1fr" : "minmax(0, 1fr)",
          gap: 14,
          marginTop: 16,
          alignItems: "start",
        }}
      >
        {/* ── Left column ── */}
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          {/* Verdict block: color square + word + reason */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div
              data-testid="space-fit-verdict-square"
              style={{
                width: 44,
                height: 44,
                borderRadius: 8,
                background: bgColor,
                flexShrink: 0,
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span
                data-testid="space-fit-verdict-text"
                style={{
                  fontSize: T.fsLg5,
                  fontWeight: T.fwBold,
                  color: hexColor,
                  lineHeight: 1.1,
                }}
              >
                {verdict ?? "—"}
              </span>
              {reason && (
                <span
                  data-testid="space-fit-verdict-reason"
                  style={{ fontSize: T.fsXs, color: T.mid, lineHeight: 1.4 }}
                >
                  {reason}
                </span>
              )}
            </div>
          </div>

          {/* Driver tiles: Assignment · Peak day · Headroom */}
          {/* OFFICE-OVERVIEW-FIXES-2: dividers removed; headroom is ±N colored */}
          <div
            data-testid="space-fit-driver-tiles"
            style={{
              display: "flex",
              marginTop: 14,
              gap: 0,
              borderTop: `1px solid ${T.lightGray}`,
            }}
          >
            <DriverTile
              label="Assignment"
              value={assignmentPct != null ? `${assignmentPct}%` : "—"}
              testId="space-fit-driver-assignment"
            />
            <DriverTile
              label="Peak utilization"
              value={
                // O-30: suppress for vacated/retired/closed stages.
                // O-34: badgeIntegrationPending no longer suppresses offices that
                // have real peak data; offices with no data naturally render "—"
                // because peakDayPct will be null.
                (location?.stage === "vacated" || location?.stage === "retired" || location?.stage === "closed")
                  ? "—"
                  : (peakDayPct != null ? `${peakDayPct}%` : "—")
              }
              testId="space-fit-driver-peak"
              attendanceCaption={attendanceCaption}
            />
            <DriverTile
              label="Unassigned"
              value={
                headroom == null
                  ? "—"
                  : headroom > 0
                  ? `+${headroom.toLocaleString()}`
                  : headroom < 0
                  ? `\u2212${Math.abs(headroom).toLocaleString()}`
                  : "0"
              }
              valueColor={
                headroom == null
                  ? undefined
                  : headroom > 0
                  ? T.greenDeep
                  : headroom < 0
                  ? T.red
                  : T.mid
              }
              testId="space-fit-driver-headroom"
            />
          </div>

          {/* Recommended line */}
          {recommended && (
            <div
              data-testid="sf-pane-recommended"
              style={{
                marginTop: 12,
                padding: "8px 10px",
                borderRadius: 6,
                background: T.lightGray,
                fontSize: T.fsXs,
                color: T.mid,
                lineHeight: 1.45,
              }}
            >
              <span style={{ fontWeight: T.fwBold, color: T.dark, display: "block", marginBottom: 2, fontSize: T.fsXxxs5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Recommended
              </span>
              {recommended}
            </div>
          )}
        </div>

        {/* The secondary full reader retains its existing portfolio chart. */}
        {showScatter && <div style={{ minWidth: 0 }}>
          {hasScatter ? (
            <>
              <SpaceFitMiniScatter
                offices={scatterOffices}
                currentLocationId={currentLocationId}
              />
              {/* OFFICE-OVERVIEW-TIGHTEN: legend moved inside right column,
                  directly beneath the scatter chart. */}
              <div
                data-testid="sf-scatter-legend"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 10,
                  marginTop: 9,
                  fontSize: T.fsXxs,
                  color: T.mid,
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: SPACE_FIT_HEX[SPACE_FIT_VERDICT_RIGHT], flexShrink: 0 }} />
                  {SPACE_FIT_LABEL[SPACE_FIT_VERDICT_RIGHT].full}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: SPACE_FIT_HEX[SPACE_FIT_VERDICT_DENSE], flexShrink: 0 }} />
                  {SPACE_FIT_LABEL[SPACE_FIT_VERDICT_DENSE].full}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: SPACE_FIT_HEX[SPACE_FIT_VERDICT_UNDER], flexShrink: 0 }} />
                  {SPACE_FIT_LABEL[SPACE_FIT_VERDICT_UNDER].full}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: SPACE_FIT_HEX[SPACE_FIT_VERDICT_OVER], flexShrink: 0 }} />
                  {SPACE_FIT_LABEL[SPACE_FIT_VERDICT_OVER].full}
                </span>
              </div>
            </>
          ) : (
            <div
              data-testid="sf-mini-scatter-placeholder"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 120,
                borderRadius: 8,
                background: T.lightGray,
                fontSize: T.fsXs,
                color: T.light,
                fontStyle: "italic",
              }}
            >
              No portfolio data
            </div>
          )}
        </div>}
      </div>
    </div>
  );
}
